import { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend,
} from 'recharts';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';
import './Analytics.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getBadgeMarge = (pct) => {
  if (pct >= 50) return { classe: 'success' };
  if (pct >= 35) return { classe: 'warning' };
  return { classe: 'danger' };
};

const fmt$ = (v) => `${(+v || 0).toFixed(2)}$`;
const fmtPct = (v) => `${(+v || 0).toFixed(1)}%`;

// Couleurs token-pures pour Recharts — constantes résolues au runtime
const COULEURS_PRODUITS = {
  saucisses:    'var(--accent)',
  porc:         'var(--warning)',
  brisket:      'var(--info)',
  burnt_ends:   'var(--danger)',
  cotes_levees: 'var(--info)',
  saumon:       'var(--success)',
  sauce:        'var(--success)',
};

const ANNEES = [2025, 2026, 2027];

// Noms des produits (sans emojis)
const LABELS_PRODUITS = {
  saucisses:    'Saucisses',
  porc:         'Porc Effiloché',
  brisket:      'Brisket',
  burnt_ends:   'Burnt Ends',
  cotes_levees: 'Côtes Levées',
  saumon:       'Saumon Fumé',
  sauce:        'Sauce BBQ',
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function AnalyticsSkeleton() {
  return (
    <div className="analytics-page rise-in">
      <div className="page-header">
        <div className="page-header-text">
          <div className="skeleton" style={{ height: 34, width: 280, marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 13, width: 200 }} />
        </div>
        <div className="skeleton" style={{ height: 34, width: 120, borderRadius: 'var(--radius-md)' }} />
      </div>
      <div className="ana-kpis-grid" style={{ marginBottom: 'var(--space-7)' }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', minHeight: 110, animationDelay: `${i * 60}ms` }}>
            <div className="skeleton" style={{ height: 11, width: '60%' }} />
            <div className="skeleton" style={{ height: 30, width: '75%' }} />
            <div className="skeleton" style={{ height: 13, width: '40%' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Composant KPI card ───────────────────────────────────────────────────────

function KpiCard({ label, valeur, unite = '', delta, children }) {
  return (
    <div className="ana-kpi-card card rise-in">
      <div className="kpi-label">{label}</div>
      <div className="ana-kpi-valeur">{valeur}{unite}</div>
      {delta !== undefined && (
        <div className={`ana-kpi-delta ${delta >= 0 ? 'success' : 'danger'}`}>
          {delta >= 0 ? '+' : ''}{Math.abs(delta).toFixed(1)}{unite}
        </div>
      )}
      {children}
    </div>
  );
}

// ─── Tooltip personnalisé ─────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label, suffix = '' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="chart-tooltip-value" style={{ color: p.color }}>
          {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}{suffix}
        </div>
      ))}
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function Analytics() {
  const [annee, setAnnee]           = useState(new Date().getFullYear());
  const [produitFiltre, setProduit] = useState('');
  const [overview, setOverview]     = useState(null);
  const [detail, setDetail]         = useState(null);
  const [couts, setCouts]           = useState(null);
  const [pointMort, setPointMort]   = useState(null);
  const [loading, setLoading]       = useState(true);
  const [erreur, setErreur]         = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur(null);
    try {
      const [ov, cm, pm] = await Promise.all([
        api.get(`/analytics/overview?annee=${annee}`),
        api.get(`/analytics/couts-matieres?mois=12${produitFiltre ? `&produit=${produitFiltre}` : ''}`),
        api.get(`/analytics/point-mort${produitFiltre ? `?produit=${produitFiltre}` : ''}`),
      ]);
      setOverview(ov);
      setCouts(cm);
      setPointMort(pm);

      if (produitFiltre) {
        const d = await api.get(`/analytics/produit/${produitFiltre}?nb_batches=10`);
        setDetail(d);
      } else {
        setDetail(null);
      }
    } catch (e) {
      console.error('Erreur analytics:', e);
      setErreur('Impossible de charger les données analytiques.');
    } finally {
      setLoading(false);
    }
  }, [annee, produitFiltre]);

  useEffect(() => { charger(); }, [charger]);

  const exporter = async () => {
    setExportLoading(true);
    try {
      const res = await fetch('/api/analytics/export-excel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('escamel_token')}`,
        },
        body: JSON.stringify({ annee }),
      });
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `Escamel_Analytics_${annee}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Erreur export:', e);
    } finally {
      setExportLoading(false);
    }
  };

  // Données pour les graphiques
  const dataMargeYield = (overview?.par_produit || []).map(p => ({
    name   : LABELS_PRODUITS[p.produit] || p.label,
    marge  : p.marge_brute_pct,
    yield  : p.yield_moyen,
    produit: p.produit,
  }));

  const dataCouts = (() => {
    if (!couts?.evolution) return [];
    const parMois = {};
    couts.evolution.forEach(r => {
      if (!parMois[r.mois]) parMois[r.mois] = { mois: r.mois };
      parMois[r.mois][r.produit] = r.prix_kg_moyen;
    });
    return Object.values(parMois).sort((a, b) => a.mois.localeCompare(b.mois));
  })();

  const produitsCouts = [...new Set((couts?.evolution || []).map(r => r.produit))];

  if (loading) return <AnalyticsSkeleton />;

  if (erreur) {
    return (
      <div className="analytics-page">
        <div className="error-inline">
          <Icon name="alerte" size={16} />
          {erreur}
        </div>
      </div>
    );
  }

  const kpis = overview?.kpis || {};

  return (
    <div className="analytics-page">
      {/* En-tête */}
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-title">Analytique de production</h1>
          <p className="page-subtitle muted">Performance, marges et coûts par batch</p>
        </div>
        <div className="ana-header-actions">
          <select
            value={annee}
            onChange={e => setAnnee(+e.target.value)}
            className="ana-select"
            aria-label="Année"
          >
            {ANNEES.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select
            value={produitFiltre}
            onChange={e => setProduit(e.target.value)}
            className="ana-select"
            aria-label="Filtrer par produit"
          >
            <option value="">Tous les produits</option>
            <option value="saucisses">Saucisses</option>
            <option value="porc">Porc Effiloché</option>
            <option value="brisket">Brisket</option>
            <option value="burnt_ends">Burnt Ends</option>
            <option value="cotes_levees">Côtes Levées</option>
            <option value="saumon">Saumon Fumé</option>
            <option value="sauce">Sauce BBQ</option>
          </select>
          <button className="btn btn-ghost" onClick={exporter} disabled={exportLoading}>
            <Icon name="telecharger" size={15} />
            {exportLoading ? 'Export…' : 'Exporter Excel'}
          </button>
        </div>
      </div>

      {/* KPIs globaux */}
      <section className="ana-section">
        <div className="card-header" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="card-header-left">
            <span className="card-header-icon"><Icon name="analytics" size={15} /></span>
            <span className="card-title">KPIs globaux — {annee}</span>
          </div>
        </div>
        <div className="ana-kpis-grid">
          <KpiCard label="Marge brute moyenne" valeur={fmtPct(kpis.marge_moyenne_globale)}>
            <span className={`badge badge--${getBadgeMarge(kpis.marge_moyenne_globale).classe}`}>
              {getBadgeMarge(kpis.marge_moyenne_globale).classe === 'success' ? 'Excellente' :
               getBadgeMarge(kpis.marge_moyenne_globale).classe === 'warning' ? 'Correcte' : 'Faible'}
            </span>
          </KpiCard>
          <KpiCard label="Yield moyen global" valeur={fmtPct(kpis.yield_moyen_global)} />
          <KpiCard label="Coût de revient moyen" valeur={fmt$(kpis.cout_revient_moyen)} unite="/unité" />
          <KpiCard label="Meilleur produit (marge)" valeur={kpis.meilleur_produit_marge || '—'}>
            <div className="muted ana-kpi-sub">{kpis.batches_ce_mois} batch(s) ce mois</div>
          </KpiCard>
        </div>
      </section>

      {/* Marge par produit */}
      {(overview?.par_produit?.length > 0) && (
        <section className="ana-section">
          <div className="card-header">
            <div className="card-header-left">
              <span className="card-header-icon"><Icon name="analytics" size={15} /></span>
              <span className="card-title">Marge brute par produit</span>
            </div>
          </div>
          <div className="ana-chart-container card">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dataMargeYield} layout="vertical" margin={{ left: 20, right: 40, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis type="number" unit="%" domain={[0, 100]} tick={{ fill: 'var(--muted)', fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={160} tick={{ fill: 'var(--text)', fontSize: 12 }} />
                <Tooltip content={<ChartTooltip suffix="%" />} />
                <Bar dataKey="marge" name="Marge brute (%)" radius={[0, 4, 4, 0]}
                  fill="var(--accent)"
                  label={{ position: 'right', fill: 'var(--text-soft)', fontSize: 12, formatter: v => `${v.toFixed(1)}%` }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Yield par produit */}
      {(overview?.par_produit?.length > 0) && (
        <section className="ana-section">
          <div className="card-header">
            <div className="card-header-left">
              <span className="card-header-icon"><Icon name="courbe" size={15} /></span>
              <span className="card-title">Yield moyen par produit</span>
            </div>
          </div>
          <div className="ana-chart-container card">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={dataMargeYield} margin={{ left: 20, right: 40, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" tick={{ fill: 'var(--text-soft)', fontSize: 11 }} />
                <YAxis unit="%" domain={[0, 110]} tick={{ fill: 'var(--muted)', fontSize: 12 }} />
                <Tooltip content={<ChartTooltip suffix="%" />} />
                <Bar dataKey="yield" name="Yield (%)" fill="var(--info)" radius={[4, 4, 0, 0]}
                  label={{ position: 'top', fill: 'var(--muted)', fontSize: 11, formatter: v => `${v.toFixed(0)}%` }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Évolution coûts matières */}
      {dataCouts.length > 0 && (
        <section className="ana-section">
          <div className="card-header">
            <div className="card-header-left">
              <span className="card-header-icon"><Icon name="courbe" size={15} /></span>
              <span className="card-title">Évolution des coûts ($/kg) — 12 derniers mois</span>
            </div>
          </div>
          <div className="ana-chart-container card">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={dataCouts} margin={{ left: 10, right: 20, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="mois" tick={{ fill: 'var(--muted)', fontSize: 11 }} />
                <YAxis unit="$" tick={{ fill: 'var(--muted)', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)' }}
                  formatter={(v, name) => [`${(+v).toFixed(2)}$/kg`, name]}
                />
                <Legend wrapperStyle={{ color: 'var(--muted)', fontSize: 12 }} />
                {produitsCouts.map(p => (
                  <Line key={p} type="monotone" dataKey={p}
                    name={LABELS_PRODUITS[p] || p}
                    stroke={COULEURS_PRODUITS[p] || 'var(--accent)'}
                    strokeWidth={2} dot={{ r: 3 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Point mort */}
      {(pointMort?.par_produit?.length > 0) && (
        <section className="ana-section">
          <div className="card-header">
            <div className="card-header-left">
              <span className="card-header-icon"><Icon name="analytics" size={15} /></span>
              <span className="card-title">Point mort par produit</span>
            </div>
            <span className="muted ana-note" style={{ fontSize: 12 }}>
              Coûts fixes estimés : {fmt$(pointMort.cout_fixe_mensuel_estime)}/mois
            </span>
          </div>
          <div className="card ana-table-card">
            <table className="esc-table">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Coût variable</th>
                  <th>Prix de vente</th>
                  <th>Contribution marg.</th>
                  <th>Point mort (unités)</th>
                  <th>Point mort (revenus)</th>
                </tr>
              </thead>
              <tbody>
                {pointMort.par_produit.map(p => (
                  <tr key={p.produit}>
                    <td>{LABELS_PRODUITS[p.produit] || p.label}</td>
                    <td>{fmt$(p.cout_variable)}/u</td>
                    <td>{fmt$(p.prix_vente)}/u</td>
                    <td className={p.contribution_marginale > 0 ? 'success' : 'danger'}>
                      {fmt$(p.contribution_marginale)}
                    </td>
                    <td><strong>{p.point_mort_unites ?? '—'}</strong></td>
                    <td>{p.point_mort_revenus ? fmt$(p.point_mort_revenus) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Détail produit sélectionné */}
      {detail && produitFiltre && (
        <section className="ana-section">
          <div className="card-header">
            <div className="card-header-left">
              <span className="card-header-icon"><Icon name="analytics" size={15} /></span>
              <span className="card-title">
                Historique — {LABELS_PRODUITS[produitFiltre] || detail.produit}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <span className={`badge badge--${getBadgeMarge(detail.moyennes?.marge_moyenne).classe}`}>
                Marge moy. {fmtPct(detail.moyennes?.marge_moyenne)}
              </span>
              <span className="badge badge--info">Tendance : {detail.tendance}</span>
            </div>
          </div>
          <div className="ana-detail-stats card">
            <div>
              <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Yield moyen</span>
              <strong className="ana-stat-value">{fmtPct(detail.moyennes?.yield_moyen)}</strong>
            </div>
            <div>
              <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Coût/unité moyen</span>
              <strong className="ana-stat-value">{fmt$(detail.moyennes?.cout_moyen)}</strong>
            </div>
            <div>
              <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Marge moyenne</span>
              <strong className="ana-stat-value">{fmtPct(detail.moyennes?.marge_moyenne)}</strong>
            </div>
            {detail.meilleur_batch && (
              <div>
                <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Meilleur batch</span>
                <strong className="ana-stat-value success">{detail.meilleur_batch.nom} ({fmtPct(detail.meilleur_batch.marge_brute_pct)})</strong>
              </div>
            )}
            {detail.pire_batch && (
              <div>
                <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Moins bon batch</span>
                <strong className="ana-stat-value danger">{detail.pire_batch.nom} ({fmtPct(detail.pire_batch.marge_brute_pct)})</strong>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Historique tous les batches */}
      <section className="ana-section">
        <div className="card-header">
          <div className="card-header-left">
            <span className="card-header-icon"><Icon name="liste" size={15} /></span>
            <span className="card-title">
              {produitFiltre && detail ? 'Historique des batches (10 derniers)' : 'Tous les batches finalisés'}
            </span>
          </div>
        </div>
        <div className="card ana-table-card">
          {(produitFiltre && detail ? detail.historique_batches : []).length === 0 && !detail ? (
            <div className="empty-state">
              <Icon name="analytics" size={32} />
              <p className="empty-state__titre">Sélectionner un produit pour voir le détail</p>
            </div>
          ) : (
            <table className="esc-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Produit</th>
                  <th>Batch</th>
                  <th>Yield</th>
                  <th>Coût/u</th>
                  <th>Marge brute</th>
                  <th>Unités</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {(produitFiltre && detail ? detail.historique_batches : []).map(b => {
                  const badge = getBadgeMarge(b.marge_brute_pct);
                  return (
                    <tr key={b.id}>
                      <td className="muted">{b.date_prod?.substring(0, 7)}</td>
                      <td>{LABELS_PRODUITS[b.produit] || b.produit}</td>
                      <td>{b.nom}</td>
                      <td>{fmtPct(b.yield_global_pct)}</td>
                      <td>{fmt$(b.cout_par_unite)}</td>
                      <td>
                        <span className={badge.classe}><strong>{fmtPct(b.marge_brute_pct)}</strong></span>
                      </td>
                      <td>{b.nb_unites_produites}</td>
                      <td>
                        <span className={`badge badge--${badge.classe}`}>
                          {badge.classe === 'success' ? 'Excellent' : badge.classe === 'warning' ? 'Correct' : 'Faible'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Tableau récap par produit */}
      {(overview?.par_produit?.length > 0) && (
        <section className="ana-section">
          <div className="card-header">
            <div className="card-header-left">
              <span className="card-header-icon"><Icon name="analytics" size={15} /></span>
              <span className="card-title">Récapitulatif par produit — {annee}</span>
            </div>
          </div>
          <div className="card ana-table-card">
            <table className="esc-table">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Batches</th>
                  <th>Yield moy.</th>
                  <th>Coût rev. moy.</th>
                  <th>Prix vente moy.</th>
                  <th>Marge brute</th>
                  <th>Coût mat. total</th>
                  <th>Revenu total</th>
                </tr>
              </thead>
              <tbody>
                {overview.par_produit.map(p => {
                  const badge = getBadgeMarge(p.marge_brute_pct);
                  return (
                    <tr key={p.produit}
                      className={produitFiltre === p.produit ? 'ana-row-active' : ''}
                      onClick={() => setProduit(produitFiltre === p.produit ? '' : p.produit)}
                      style={{ cursor: 'pointer' }}>
                      <td><strong>{LABELS_PRODUITS[p.produit] || p.label}</strong></td>
                      <td>{p.nb_batches}</td>
                      <td>{fmtPct(p.yield_moyen)}</td>
                      <td>{fmt$(p.cout_revient_moyen)}/u</td>
                      <td>{fmt$(p.prix_vente_moyen)}/u</td>
                      <td>
                        <span className={`badge badge--${badge.classe}`}>
                          <strong>{fmtPct(p.marge_brute_pct)}</strong>
                        </span>
                      </td>
                      <td>{fmt$(p.cout_matieres_total)}</td>
                      <td className="success">{fmt$(p.revenu_total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
